// src/components/patient/appointments/Dialog.jsx
//
// Application modal (never window.confirm/prompt). Focus moves in on open,
// Tab is trapped, Escape closes unless a mutation is running, and focus returns
// to the element that opened it.

import React, { useEffect, useRef } from "react";

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export default function Dialog({ open, title, labelId, busy = false, onClose, children, wide = false }) {
  const ref = useRef(null);
  const busyRef = useRef(busy);
  const closeRef = useRef(onClose);
  busyRef.current = busy;
  closeRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    const opener = document.activeElement;
    const first = ref.current?.querySelector("[data-autofocus]") || ref.current?.querySelector(FOCUSABLE);
    first?.focus();
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        if (!busyRef.current) closeRef.current?.();
        return;
      }
      if (event.key !== "Tab") return;
      const nodes = Array.from(ref.current?.querySelectorAll(FOCUSABLE) || []);
      if (!nodes.length) return;
      const firstNode = nodes[0];
      const lastNode = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === firstNode) {
        event.preventDefault();
        lastNode.focus();
      } else if (!event.shiftKey && document.activeElement === lastNode) {
        event.preventDefault();
        firstNode.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      if (opener && typeof opener.focus === "function") opener.focus();
    };
  }, [open]);

  if (!open) return null;
  const headingId = labelId || "appointment-dialog-title";
  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center bg-slate-900/50 p-0 sm:items-center sm:p-4">
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        aria-busy={busy || undefined}
        className={`max-h-[92vh] w-full overflow-y-auto rounded-t-3xl bg-white p-5 shadow-xl sm:rounded-3xl sm:p-6 ${wide ? "sm:max-w-3xl" : "sm:max-w-lg"}`}
      >
        <div className="flex items-start justify-between gap-3">
          <h2 id={headingId} className="text-lg font-semibold">{title}</h2>
          <button type="button" onClick={() => !busy && onClose?.()} disabled={busy} aria-label="Close" className="rounded-xl px-2 py-1 text-lg leading-none text-slate-500 hover:bg-slate-100 disabled:opacity-40">
            ×
          </button>
        </div>
        <div className="mt-3">{children}</div>
      </div>
    </div>
  );
}
